import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocale } from '@/context/LocaleContext';
import { characterAvatarUrl } from '@/lib/assetUrl';
import { api } from '@/lib/axios';
import {
  useMarkRead,
  useSendMessage,
  useMessagesThread,
  messagesThreadQueryKey,
  upsertConversationInCache,
  scheduleConversationsSync,
  type ChatMessage,
  type MessageInventoryItem,
} from '@/hooks/useMessages';
import { sendTyping } from '@/hooks/useMessagesWebSocket';
import { useItemTooltipHover } from '@/hooks/useItemTooltipHover';
import { MessageInput, type AttachedItem } from '@/pages/messages/MessageInput';
import { MessageThread } from '@/pages/messages/MessageThread';
import { filterThreadMessages, messageBelongsToThread } from '@/pages/messages/messageBelongsToThread';
import { sortMessagesChronologically } from '@/pages/messages/messageUtils';

export interface ChatSessionProps {
  otherCharId: number;
  otherCharName: string;
  otherRefObjId: number;
  currentCharId: number;
  attachedItem: AttachedItem | null;
  onAttachClick: () => void;
  onRemoveAttached: () => void;
  partnerStatus: 'online' | 'offline';
  isTyping: boolean;
}

/**
 * Sesión aislada por conversación (key={otherCharId} en el padre).
 * Al cambiar de chat este componente se desmonta por completo — evita historial cruzado.
 */
export function ChatSession({
  otherCharId,
  otherCharName,
  otherRefObjId,
  currentCharId,
  attachedItem,
  onAttachClick,
  onRemoveAttached,
  partnerStatus,
  isTyping,
}: ChatSessionProps) {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const sendMutation = useSendMessage();
  const markReadMutation = useMarkRead(currentCharId);

  const [sendError, setSendError] = useState('');
  const [extraMessages, setExtraMessages] = useState<ChatMessage[]>([]);
  const [messagesOffset, setMessagesOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const sessionCharIdRef = useRef(otherCharId);
  sessionCharIdRef.current = otherCharId;

  const { data: threadRaw, isPending, isFetching, isFetched, isError, refetch } = useMessagesThread(
    otherCharId,
    currentCharId,
  );

  const threadMessages = useMemo(() => {
    if (!isFetched || !threadRaw) return [];
    return filterThreadMessages(threadRaw, currentCharId, otherCharId);
  }, [threadRaw, isFetched, currentCharId, otherCharId]);

  const allMessages = useMemo(() => {
    const base = threadMessages;
    if (extraMessages.length === 0) return sortMessagesChronologically(base);
    const ids = new Set(base.map((m) => m.id));
    const older = extraMessages.filter(
      (m) => !ids.has(m.id) && messageBelongsToThread(m, currentCharId, otherCharId),
    );
    return sortMessagesChronologically([...older, ...base]);
  }, [threadMessages, extraMessages, currentCharId, otherCharId]);

  const tooltipKey = allMessages.filter((m) => m.id64).map((m) => m.id64).join(',');
  useItemTooltipHover(allMessages.some((m) => m.id64), tooltipKey);

  useEffect(() => {
    markReadMutation.mutate(otherCharId);
  }, [otherCharId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentCharId <= 0 || otherCharId <= 0) return;
    const hasUnreadIncoming = allMessages.some(
      (m) =>
        m.receiver_CharID === currentCharId &&
        m.sender_CharID === otherCharId &&
        m.status === 'unread',
    );
    if (hasUnreadIncoming) {
      markReadMutation.mutate(otherCharId);
    }
  }, [allMessages, currentCharId, otherCharId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMoreMessages = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    const chatId = sessionCharIdRef.current;
    setIsLoadingMore(true);
    const nextOffset = messagesOffset + 50;
    try {
      const { data } = await api.get<{ status: string; data: ChatMessage[] }>(
        `/api/v1/messages/${chatId}`,
        { params: { offset: nextOffset, limit: 50 } },
      );
      if (chatId !== sessionCharIdRef.current) return;

      const batch = filterThreadMessages(data.data ?? [], currentCharId, chatId);
      if (batch.length === 0) {
        setHasMore(false);
      } else {
        setExtraMessages((prev) => {
          const ids = new Set([...prev, ...threadMessages].map((m) => m.id));
          return [...batch.filter((m) => !ids.has(m.id)), ...prev];
        });
        setMessagesOffset(nextOffset);
        if (batch.length < 50) setHasMore(false);
      }
    } catch {
      setSendError(t('messages_error_load'));
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, messagesOffset, threadMessages, currentCharId]);

  const handleSend = async (text: string) => {
    if (currentCharId <= 0 || otherCharId === currentCharId) return false;
    setSendError('');

    try {
      const result = await sendMutation.mutateAsync({
        receiver: otherCharId,
        message: text,
        id64: attachedItem?.id64 ?? null,
      });

      const wasNew = upsertConversationInCache(queryClient, currentCharId, {
        otherCharID: otherCharId,
        otherCharName: otherCharName,
        otherRefObjID: otherRefObjId,
        message: text,
        created_at: result.timestamp,
        senderCharID: currentCharId,
      });
      scheduleConversationsSync(queryClient, currentCharId, wasNew ? 1200 : 1500);

      queryClient.setQueryData<ChatMessage[]>(
        messagesThreadQueryKey(currentCharId, otherCharId),
        (prev) => {
          const list = prev ?? [];
          if (list.some((m) => m.id === result.messageId)) return sortMessagesChronologically(list);
          const row: ChatMessage = {
            id: result.messageId,
            sender_CharID: currentCharId,
            receiver_CharID: otherCharId,
            id64: attachedItem?.id64 ?? null,
            message: text,
            status: 'unread',
            created_at: result.timestamp,
            senderName: '',
            senderRefObjID: 0,
            receiverName: otherCharName,
            receiverRefObjID: otherRefObjId,
            itemName: attachedItem?.name ?? null,
            itemIcon: attachedItem?.icon,
            isLocked: 0,
          };
          return sortMessagesChronologically([...list, row]);
        },
      );

      onRemoveAttached();
      return true;
    } catch {
      setSendError(t('messages_error_send'));
      return false;
    }
  };

  const statusLabel = isTyping
    ? t('messages_typing')
    : partnerStatus === 'online'
      ? t('messages_online')
      : t('messages_offline');

  const showLoading = isPending || (isFetching && !isFetched);
  const visibleMessages = showLoading ? [] : allMessages;
  const showLoadError = isError && !showLoading && visibleMessages.length === 0;

  return (
    <div className="chat-active-window" id="chat-active">
      <div className="chat-header">
        <div className="chat-user-info">
          <img
            src={characterAvatarUrl(otherRefObjId)}
            alt=""
            className="chat-user-avatar"
            id="chat-user-avatar"
          />
          <div className="chat-user-details">
            <span className="chat-user-name" id="chat-user-name">
              {otherCharName}
            </span>
            <span
              className={`chat-user-status${isTyping ? ' typing-indicator' : ''}`}
              id="chat-user-status"
            >
              {statusLabel}
            </span>
          </div>
        </div>
      </div>

      <MessageThread
        messages={visibleMessages}
        currentCharId={currentCharId}
        isLoading={showLoading}
        loadError={showLoadError}
        onRetry={() => void refetch()}
        onLoadMore={() => void loadMoreMessages()}
        hasMore={hasMore && threadMessages.length >= 50}
        isLoadingMore={isLoadingMore}
      />

      {sendError && <div className="messages-send-error" role="alert">{sendError}</div>}
      <MessageInput
        sendTitle={t('messages_send')}
        placeholder={t('messages_type_message')}
        attachTitle={t('messages_attach_item')}
        attachedItem={attachedItem}
        onAttachClick={onAttachClick}
        onRemoveAttached={onRemoveAttached}
        onSend={handleSend}
        onTyping={() => sendTyping(otherCharId)}
        disabled={sendMutation.isPending}
      />
    </div>
  );
}
